// ═══════════════════════════════════════════════════════════════
// ZoneListPanel — Sidebar list of zones for map editor
// ═══════════════════════════════════════════════════════════════

import { Paper, Stack, Group, Text, Badge, ActionIcon, ScrollArea } from '../../components/gab/GabComponents';
import { IconEye, IconEyeOff, IconTrash, IconEdit } from '@tabler/icons-react';
import type { GeoOpsZone } from '../../store/geoopsStore';
import { ZONE_TYPE_META } from '../../store/geoopsStore';

interface ZoneListPanelProps {
  zones: GeoOpsZone[];
  selectedZoneId: number | null;
  hiddenZoneIds: Set<number>;
  onSelect: (zone: GeoOpsZone) => void;
  onToggleVisibility: (id: number) => void;
  onDelete: (id: number) => void;
  onEdit: (zone: GeoOpsZone) => void;
  canEdit: boolean;
}

export default function ZoneListPanel({
  zones, selectedZoneId, hiddenZoneIds,
  onSelect, onToggleVisibility, onDelete, onEdit, canEdit,
}: ZoneListPanelProps) {
  if (zones.length === 0) {
    return (
      <Paper p="md" withBorder>
        <Text size="sm" c="dimmed" ta="center">
          No zones yet. Use the drawing tools to create one.
        </Text>
      </Paper>
    );
  }

  return (
    <ScrollArea h="100%" offsetScrollbars>
      <Stack gap={4}>
        {zones.map(zone => {
          const meta = ZONE_TYPE_META[zone.zoneType] || ZONE_TYPE_META.custom;
          const isSelected = zone.id === selectedZoneId;
          const isHidden = hiddenZoneIds.has(zone.id);
          return (
            <Paper key={zone.id} p="xs" withBorder
              style={{
                cursor: 'pointer',
                borderColor: isSelected ? meta.color : undefined,
                borderWidth: isSelected ? 2 : 1,
                opacity: isHidden ? 0.5 : 1,
              }}
              onClick={() => onSelect(zone)}>
              <Group justify="space-between" wrap="nowrap">
                <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                  <div style={{
                    width: 10, height: 10, borderRadius: 2, flexShrink: 0,
                    backgroundColor: zone.color || meta.color,
                  }} />
                  <div style={{ minWidth: 0 }}>
                    <Text size="xs" fw={600} truncate>{zone.name}</Text>
                    <Badge size="xs" variant="light" color={meta.mantine}>{meta.label}</Badge>
                  </div>
                </Group>
                <Group gap={2} wrap="nowrap">
                  <ActionIcon size="xs" variant="subtle" color="gray"
                    onClick={e => { e.stopPropagation(); onToggleVisibility(zone.id); }}>
                    {isHidden ? <IconEyeOff size={12} /> : <IconEye size={12} />}
                  </ActionIcon>
                  {canEdit && (
                    <>
                      <ActionIcon size="xs" variant="subtle" color="blue"
                        onClick={e => { e.stopPropagation(); onEdit(zone); }}>
                        <IconEdit size={12} />
                      </ActionIcon>
                      <ActionIcon size="xs" variant="subtle" color="red"
                        onClick={e => { e.stopPropagation(); onDelete(zone.id); }}>
                        <IconTrash size={12} />
                      </ActionIcon>
                    </>
                  )}
                </Group>
              </Group>
            </Paper>
          );
        })}
      </Stack>
    </ScrollArea>
  );
}
